<template>
  <div>
    <div class="alert alert-danger" v-if="errorMsg">{{ errorMsg }}</div>

    <div class="row mb-2">
      <div class="col-auto">
        <button class="btn btn-primary gdq_faded gdq-font-size-80" @click="addNew">
          <span class="icon-plus mr-1"></span>Add term
        </button>
      </div>
    </div>

    <div class="card mb-3" v-if="showForm">
      <div class="card-body">
        <h6 class="card-title">{{ editing.id > 0 ? 'Edit term' : 'New term' }}</h6>
        <div class="row">
          <div class="col-12 col-md-4">
            <div class="mb-3 d-flex align-items-center">
              <label class="col-form-label" style="width: 50%; flex-shrink: 0">Section</label>
              <div style="width: 50%">
                <select class="form-select" v-model="editing.section">
                  <option value="practical">Practical</option>
                  <option value="payment">Payment &amp; cancellation</option>
                </select>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-8">
            <div class="mb-3 d-flex align-items-center">
              <label class="col-form-label" style="width: 25%; flex-shrink: 0">Title</label>
              <div style="width: 75%"><input class="form-control" v-model="editing.title" /></div>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label class="col-form-label d-block mb-2">Body (HTML)</label>
          <VueEditor v-model="editing.body" />
        </div>
        <div>
          <button class="btn btn-outline-secondary" @click="cancel">Cancel</button>
          <button class="btn btn-primary float-end" :disabled="saving" @click="save">
            <fa-icon name="check" />Save
          </button>
        </div>
      </div>
    </div>

    <div v-for="sec in sections" :key="sec.id" class="mb-4" v-show="rowsFor(sec.id).length > 0">
      <h6 class="text-uppercase text-muted">{{ sec.label }}</h6>
      <table class="table table-hover lh-table">
        <thead>
          <tr>
            <th style="width: 40px;">#</th>
            <th>Title</th>
            <th>Body</th>
            <th style="width: 110px;">⚡</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in rowsFor(sec.id)" :key="item.id" style="cursor: pointer;" @click="editRow(item)">
            <td>{{ item.sort }}</td>
            <td style="min-width: 200px;">{{ item.title }}</td>
            <td class="text-muted">{{ plain(item.body) }}</td>
            <td>
              <span class="me-2" style="cursor: pointer;" title="Move up" v-if="i > 0" @click.prevent.stop="move(item, -1)">
                <fa-icon name="arrow-up" />
              </span>
              <span class="me-2" style="cursor: pointer;" title="Move down" v-if="i < rowsFor(sec.id).length - 1" @click.prevent.stop="move(item, 1)">
                <fa-icon name="arrow-down" />
              </span>
              <span style="cursor: pointer;" title="Delete" @click.prevent.stop="delRow(item)">
                <fa-icon name="trash-alt" />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="text-center text-muted py-3" v-if="rows.length === 0">No terms</div>
  </div>
</template>

<script>
import VueEditor from '../CustomControls/VueEditor.vue'

export default {
  name: 'LHCourseTerms',
  components: { VueEditor },
  props: {
    courseId: { type: Number, required: true },
    rows: { type: Array, required: true }
  },
  data () {
    return {
      showForm: false,
      saving: false,
      errorMsg: '',
      sections: [
        { id: 'practical', label: 'Practical' },
        { id: 'payment', label: 'Payment & cancellation' }
      ],
      editing: this.emptyRow()
    }
  },
  methods: {
    emptyRow () {
      return { id: 0, sort: 0, section: 'practical', title: '', body: '' }
    },
    rowsFor (section) {
      return this.rows.filter(x => x.section === section)
    },
    plain (html) {
      return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 120)
    },
    addNew () {
      this.errorMsg = ''
      this.editing = this.emptyRow()
      this.editing.sort = this.rows.length + 1
      this.showForm = true
    },
    editRow (r) {
      this.errorMsg = ''
      this.editing = { ...r }
      this.showForm = true
    },
    cancel () {
      this.showForm = false
    },
    async save () {
      this.saving = true
      this.errorMsg = ''
      try {
        const body = {
          sort: this.editing.sort,
          section: this.editing.section,
          title: this.editing.title,
          body: this.editing.body
        }
        if (this.editing.id > 0) {
          await this.$store.dispatch('learninghub_term_update', { id: this.editing.id, body })
        } else {
          body.course_id = this.courseId
          await this.$store.dispatch('learninghub_term_save', { body })
        }
        this.showForm = false
        this.$emit('changed')
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Save failed')
      }
      this.saving = false
    },
    async delRow (r) {
      if (!confirm('Delete term "' + r.title + '"?')) return
      try {
        await this.$store.dispatch('learninghub_term_delete', { id: r.id })
        this.$emit('changed')
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Delete failed')
      }
    },
    async move (item, dir) {
      const section = item.section
      const list = this.rowsFor(section)
      const i = list.findIndex(x => x.id === item.id)
      const j = i + dir
      if (j < 0 || j >= list.length) return
      const ids = this.rows.map(x => x.id)
      const ia = ids.indexOf(list[i].id)
      const ib = ids.indexOf(list[j].id)
      ;[ids[ia], ids[ib]] = [ids[ib], ids[ia]]
      try {
        await this.$store.dispatch('learninghub_reorder', { body: { entity: 'term', ids } })
        this.$emit('changed')
      } catch (e) {
        this.errorMsg = e.message || 'Reorder failed'
      }
    }
  }
}
</script>

<style>
.lh-table {
  font-size: 0.9rem !important;
}
</style>
